import type {
  ApiResponse,
  VerifyBillingEmailRequest,
  VerifyBillingEmailResult,
} from '@fluenceur/types';
import { useMutation } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import api from '@/libs/axiosInstance';
import { z } from 'zod';
import { BillingTargetChangedError, useBillingActionScope } from './use-billing-action-scope';
import { toaster } from '@/components/ui/toaster';
import { useTranslation } from 'react-i18next';
import { billingEmailError } from '@/pages/onboarding/billing/billing-email-error';

export const BILLING_EMAIL_VERIFY_MUTATION_KEY = ['billingEmailVerify'] as const;

const resultSchema = z.object({ email: z.string().email(), verified: z.literal(true) }).strict();

export function useVerifyBillingEmailAction() {
  const { t } = useTranslation();
  const { scope, assertCurrent } = useBillingActionScope();
  return useMutation<
    VerifyBillingEmailResult,
    AxiosError<ApiResponse> | Error,
    VerifyBillingEmailRequest
  >({
    mutationKey: [...BILLING_EMAIL_VERIFY_MUTATION_KEY, scope.organizationId, scope.brandId],
    retry: false,
    mutationFn: async (payload) => {
      assertCurrent(scope);
      const { data } = await api.post<ApiResponse<unknown>>(
        '/dashboard/billing/email-verification/verify',
        payload
      );
      assertCurrent(scope);
      const result = resultSchema.safeParse(data.data);
      if (data.success !== true || !result.success)
        throw new Error('Invalid email verification response');
      return result.data;
    },
    onError: (error) => {
      if (error instanceof BillingTargetChangedError) return;
      toaster.error({
        title: t('common.error'),
        description: t(billingEmailError(error, 'signup.verify.errors.invalid')),
      });
    },
  });
}
